import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth';
import { QuestionSet, AiGameSession, AiGameCreationData } from '@/types';

interface AiGameCreationModalProps {
  onCancel?: () => void;
  onGameStarted?: (session: AiGameSession) => void;
}

const AiGameCreationModal = ({ onCancel, onGameStarted }: AiGameCreationModalProps) => {
  const { questionSets, user } = useAuthStore();

  // Step navigation
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);

  // Step 1: Question Set Selection
  const [selectedQuestionSet, setSelectedQuestionSet] = useState<QuestionSet | null>(null);

  // Step 2: AI Configuration
  const [allowStudentChoice, setAllowStudentChoice] = useState(true);
  const [aiPlayerCount, setAiPlayerCount] = useState(3);

  // Step 3: Duration
  const [selectedPreset, setSelectedPreset] = useState<'24h' | '3d' | '1w' | 'custom' | null>(null);
  const [customEndDate, setCustomEndDate] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');

  const totalSteps = 3;

  const calculateEndDate = (preset: '24h' | '3d' | '1w' | null): Date | null => {
    if (!preset) return null;

    const now = new Date();
    switch (preset) {
      case '24h':
        return new Date(now.getTime() + 24 * 60 * 60 * 1000);
      case '3d':
        return new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
      case '1w':
        return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      default:
        return null;
    }
  };

  const formatEndDate = (date: Date | null): string => {
    if (!date) return '';

    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    return date.toLocaleString('en-US', options);
  };

  const getCustomEndDate = (): Date | null => {
    if (!customEndDate || !customEndTime) return null;
    return new Date(`${customEndDate}T${customEndTime}`);
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateAiGame = async () => {
    if (!selectedQuestionSet || !user) return;

    try {
      setIsCreating(true);

      const endDate = selectedPreset === 'custom' ? getCustomEndDate() : calculateEndDate(selectedPreset);

      if (!endDate) {
        throw new Error('End date is required');
      }

      console.log('🤖 Creating AI game with:', {
        questionSet: selectedQuestionSet.name,
        allowStudentChoice,
        aiCount: allowStudentChoice ? 'Student decides' : aiPlayerCount,
        endDate: formatEndDate(endDate)
      });

      // Create AI game session in Firebase
      const aiGameData: AiGameCreationData = {
        questionSetId: selectedQuestionSet.id,
        allowStudentChoice,
        fixedAiPlayerCount: allowStudentChoice ? undefined : aiPlayerCount,
        endDate
      };

      const createdSession = await authService.createAiGameSession(aiGameData, user.uid);

      console.log('✅ AI game session created:', createdSession);

      onGameStarted?.(createdSession);
    } catch (error: unknown) {
      console.error('Failed to create AI game:', error);
      alert(`Failed to create AI game: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCreating(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedQuestionSet !== null;
      case 2: return true; // Always can proceed from step 2
      case 3: {
        if (!selectedPreset) return false;
        if (selectedPreset === 'custom') {
          return customEndDate !== '' && customEndTime !== '';
        }
        return true;
      }
      default: return false;
    }
  };

  const renderStepIndicator = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '2rem',
      gap: '1rem'
    }}>
      {Array.from({ length: totalSteps }, (_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: '2.5rem',
            height: '2.5rem',
            borderRadius: '50%',
            background: i + 1 <= currentStep
              ? 'linear-gradient(135deg, #f97316, #ea580c)'
              : '#e5e7eb',
            color: i + 1 <= currentStep ? 'white' : '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            {i + 1 <= currentStep ? '✓' : i + 1}
          </div>
          {i < totalSteps - 1 && (
            <div style={{
              width: '3rem',
              height: '2px',
              background: i + 1 < currentStep
                ? 'linear-gradient(135deg, #f97316, #ea580c)'
                : '#e5e7eb',
              marginLeft: '0.5rem',
              marginRight: '0.5rem'
            }} />
          )}
        </div>
      ))}
    </div>
  );

  // Step 1: Question Set Selection (identical to multiplayer)
  const renderStep1 = () => (
    <div>
      <h3 style={{
        color: '#374151',
        marginBottom: '1rem',
        fontSize: '1.3rem',
        fontWeight: '600',
        textAlign: 'center'
      }}>
        📚 Select Question Set
      </h3>
      <p style={{
        color: '#64748b',
        textAlign: 'center',
        marginBottom: '2rem'
      }}>
        Choose the question set you want to use for this AI game
      </p>

      {questionSets.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#64748b'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
          <h4 style={{ color: '#374151', marginBottom: '0.5rem' }}>No question sets available</h4>
          <p>You need to create question sets before starting an AI game.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '1rem',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          {questionSets.map((questionSet) => (
            <div
              key={questionSet.id}
              onClick={() => setSelectedQuestionSet(questionSet)}
              style={{
                background: selectedQuestionSet?.id === questionSet.id
                  ? 'rgba(249, 115, 22, 0.1)'
                  : 'rgba(255, 255, 255, 0.7)',
                border: selectedQuestionSet?.id === questionSet.id
                  ? '2px solid #f97316'
                  : '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                if (selectedQuestionSet?.id !== questionSet.id) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseOut={(e) => {
                if (selectedQuestionSet?.id !== questionSet.id) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.5rem'
              }}>
                <h4 style={{
                  margin: 0,
                  color: '#374151',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  {questionSet.name}
                </h4>
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center'
                }}>
                  <span style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {questionSet.totalQuestions} questions
                  </span>
                  <span style={{
                    background: questionSet.difficulty === 'easy' ? '#dcfce7' :
                             questionSet.difficulty === 'medium' ? '#fef3c7' :
                             questionSet.difficulty === 'hard' ? '#fee2e2' : '#f3f4f6',
                    color: questionSet.difficulty === 'easy' ? '#166534' :
                           questionSet.difficulty === 'medium' ? '#92400e' :
                           questionSet.difficulty === 'hard' ? '#991b1b' : '#374151',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {questionSet.difficulty}
                  </span>
                </div>
              </div>
              {questionSet.description && (
                <p style={{
                  color: '#64748b',
                  margin: '0.5rem 0',
                  fontSize: '0.9rem'
                }}>
                  {questionSet.description}
                </p>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                fontSize: '0.8rem',
                color: '#64748b',
                marginTop: '1rem'
              }}>
                <span>📅 Created {new Date(questionSet.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Step 2: AI Configuration
  const renderStep2 = () => (
    <div>
      <h3 style={{
        color: '#374151',
        marginBottom: '1rem',
        fontSize: '1.3rem',
        fontWeight: '600',
        textAlign: 'center'
      }}>
        🤖 AI Opponents
      </h3>
      <p style={{
        color: '#64748b',
        textAlign: 'center',
        marginBottom: '2rem'
      }}>
        Choose how many AI players will compete against the student
      </p>

      <div style={{
        background: 'rgba(249, 115, 22, 0.1)',
        borderRadius: '12px',
        padding: '1.5rem'
      }}>
        {/* Student Choice Checkbox */}
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          cursor: 'pointer',
          padding: '1rem',
          borderRadius: '8px',
          background: allowStudentChoice ? 'rgba(249, 115, 22, 0.15)' : 'transparent',
          border: allowStudentChoice ? '2px solid #f97316' : '1px solid transparent',
          marginBottom: '1.5rem'
        }}>
          <input
            type="checkbox"
            checked={allowStudentChoice}
            onChange={(e) => setAllowStudentChoice(e.target.checked)}
            style={{
              width: '1.2rem',
              height: '1.2rem',
              cursor: 'pointer',
              accentColor: '#f97316'
            }}
          />
          <div>
            <div style={{ color: '#374151', fontWeight: '600', fontSize: '1rem' }}>
              👥 Let Students Decide
            </div>
            <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.25rem' }}>
              Students can choose how many AI opponents they want when they start the game
            </div>
          </div>
        </label>

        {/* Teacher-Controlled AI Count */}
        {!allowStudentChoice && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '8px',
            padding: '1.5rem'
          }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontWeight: '600',
              marginBottom: '1rem',
              fontSize: '0.95rem'
            }}>
              🎮 Number of AI Opponents (Fixed)
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={aiPlayerCount}
              onChange={(e) => setAiPlayerCount(parseInt(e.target.value))}
              style={{
                width: '100%',
                marginBottom: '0.75rem',
                accentColor: '#f97316'
              }}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>1 AI</span>
              <span style={{
                background: '#f97316',
                color: 'white',
                padding: '0.4rem 1rem',
                borderRadius: '20px',
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                {aiPlayerCount} AI {aiPlayerCount === 1 ? 'Opponent' : 'Opponents'}
              </span>
              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>10 AI</span>
            </div>
          </div>
        )}

        {/* Info Note */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.6)',
          borderRadius: '8px',
          padding: '0.75rem',
          fontSize: '0.85rem',
          color: '#64748b',
          marginTop: '1rem',
          borderLeft: '3px solid #f97316'
        }}>
          <strong style={{ color: '#374151' }}>ℹ️ Note:</strong> AI players compete with varying skill levels (70-100% accuracy) to create a realistic challenge.
        </div>
      </div>
    </div>
  );

  // Step 3: Duration
  const renderStep3 = () => {
    const presetEndDate = selectedPreset && selectedPreset !== 'custom'
      ? calculateEndDate(selectedPreset)
      : null;
    const customDate = getCustomEndDate();
    const displayEndDate = selectedPreset === 'custom' ? customDate : presetEndDate;

    return (
      <div>
        <h3 style={{
          color: '#374151',
          marginBottom: '1rem',
          fontSize: '1.3rem',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          ⏰ Game Duration
        </h3>
        <p style={{
          color: '#64748b',
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          How long should this AI game be available for students?
        </p>

        {/* Preset Duration Boxes */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {[
            { id: '24h' as const, label: '24 Hours', icon: '⏱️' },
            { id: '3d' as const, label: '3 Days', icon: '📅' },
            { id: '1w' as const, label: '1 Week', icon: '🗓️' }
          ].map((preset) => (
            <button
              key={preset.id}
              onClick={() => {
                setSelectedPreset(preset.id);
                setCustomEndDate('');
                setCustomEndTime('');
              }}
              style={{
                background: selectedPreset === preset.id
                  ? 'linear-gradient(135deg, #f97316, #ea580c)'
                  : 'rgba(255, 255, 255, 0.7)',
                color: selectedPreset === preset.id ? 'white' : '#374151',
                border: selectedPreset === preset.id ? '2px solid #f97316' : '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                padding: '1.25rem',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                boxShadow: selectedPreset === preset.id ? '0 4px 12px rgba(249, 115, 22, 0.3)' : 'none'
              }}
              onMouseOver={(e) => {
                if (selectedPreset !== preset.id) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }
              }}
              onMouseOut={(e) => {
                if (selectedPreset !== preset.id) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{preset.icon}</div>
              <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{preset.label}</div>
            </button>
          ))}
        </div>

        {/* Display End Date/Time */}
        {displayEndDate && selectedPreset !== 'custom' && (
          <div style={{
            background: 'rgba(249, 115, 22, 0.1)',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '2rem',
            textAlign: 'center',
            border: '2px solid rgba(249, 115, 22, 0.3)'
          }}>
            <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Game will end on:
            </div>
            <div style={{
              color: '#f97316',
              fontSize: '1.1rem',
              fontWeight: '600'
            }}>
              {formatEndDate(displayEndDate)}
            </div>
          </div>
        )}

        {/* Custom Date/Time Picker */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.7)',
          borderRadius: '12px',
          padding: '1.5rem',
          border: selectedPreset === 'custom' ? '2px solid #f97316' : '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem'
          }}>
            <input
              type="radio"
              id="custom"
              checked={selectedPreset === 'custom'}
              onChange={() => setSelectedPreset('custom')}
              style={{
                width: '1.2rem',
                height: '1.2rem',
                cursor: 'pointer',
                accentColor: '#f97316'
              }}
            />
            <label
              htmlFor="custom"
              style={{
                color: '#374151',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: 'pointer'
              }}
            >
              🎯 Custom End Date & Time
            </label>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            opacity: selectedPreset === 'custom' ? 1 : 0.5,
            pointerEvents: selectedPreset === 'custom' ? 'auto' : 'none'
          }}>
            <div>
              <label style={{
                display: 'block',
                color: '#64748b',
                fontSize: '0.85rem',
                marginBottom: '0.5rem',
                fontWeight: '600'
              }}>
                📅 Date
              </label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.9rem',
                  color: '#374151'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                color: '#64748b',
                fontSize: '0.85rem',
                marginBottom: '0.5rem',
                fontWeight: '600'
              }}>
                ⏰ Time
              </label>
              <input
                type="time"
                value={customEndTime}
                onChange={(e) => setCustomEndTime(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.9rem',
                  color: '#374151'
                }}
              />
            </div>
          </div>

          {/* Display Custom End Date */}
          {selectedPreset === 'custom' && customDate && (
            <div style={{
              marginTop: '1rem',
              background: 'rgba(249, 115, 22, 0.1)',
              borderRadius: '8px',
              padding: '0.75rem',
              textAlign: 'center',
              border: '1px solid rgba(249, 115, 22, 0.3)'
            }}>
              <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                Game will end on:
              </div>
              <div style={{
                color: '#f97316',
                fontSize: '0.95rem',
                fontWeight: '600'
              }}>
                {formatEndDate(customDate)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      default: return null;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        padding: '2rem',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            color: '#374151',
            margin: '0 0 0.5rem 0',
            fontSize: '1.5rem',
            fontWeight: '700'
          }}>
            🤖 Create AI Game
          </h2>
          <p style={{
            color: '#64748b',
            margin: 0,
            fontSize: '0.9rem'
          }}>
            Step {currentStep} of {totalSteps}
          </p>
        </div>

        {renderStepIndicator()}
        {renderCurrentStep()}

        {/* Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '2rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={currentStep === 1 ? onCancel : handleBack}
            style={{
              background: 'transparent',
              color: '#64748b',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {currentStep === 1 ? 'Cancel' : '← Back'}
          </button>

          <button
            onClick={currentStep === totalSteps ? handleCreateAiGame : handleNext}
            disabled={!canProceed() || isCreating}
            style={{
              background: canProceed() && !isCreating
                ? 'linear-gradient(135deg, #f97316, #ea580c)'
                : '#d1d5db',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: canProceed() && !isCreating ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: canProceed() && !isCreating ? '0 4px 12px rgba(249, 115, 22, 0.3)' : 'none'
            }}
          >
            {isCreating ? (
              <>🔄 Creating...</>
            ) : currentStep === totalSteps ? (
              <>🤖 Create AI Game</>
            ) : (
              <>Next →</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiGameCreationModal;
